import React, { ReactElement, useState } from "react";
import { StepProps } from "../TradeModal";
import SmallDataTable from "components/SmallDataTable/SmallDataTable";
import styles from "./TradePreview.module.scss";
import { formatCurrency } from "@tiicker/util/lib/formats";
import { TradeOptions } from "../TradeStock/TradeTypes";
import { useMutationBundle } from "source/hooks";
import { ExecuteTrade } from "$gql/mutations/tradier/ExecuteTrade.gen";
import BrokerConnectionMessage from "components/BrokerConnectionMessage/BrokerConnectionMessage";
import Button from "components/core/Button/Button";

export default function TradePreview(props: StepProps): ReactElement {
  const { tradePreview, order, institutionAccountId } = props.state;
  if (!tradePreview || !order || !institutionAccountId) {
    throw new Error(
      "Preview, order, and institutionAccountId are required for this step"
    );
  }
  props.setTitle("Review");

  const [isLoading, setIsLoading] = useState(false);

  const [executeMutation] = useMutationBundle(ExecuteTrade);

  const onSubmit = React.useCallback(() => {
    setIsLoading(true);
    props.state.errorMessage = null;

    executeMutation({
      variables: {
        options: {
          ...order,
          // price: tradePreview?.orderCost / tradePreview?.quantity,
          previewId: tradePreview?.previewId?.toString(),
        },
        institutionAccountId,
      },
    })
      .then((r) => {
        setIsLoading(false);
        props.dispatch({
          type: "executedTrade",
          payload: { orderNumber: r.data?.executeTrade.id || -1 },
        });
      })
      .catch((r) => {
        setIsLoading(false);
        console.error(r.message);
        props.dispatch({
          type: "errorEncountered",
          payload: {
            errorMessage:
              r?.message ||
              "There was an error while trying to complete your trade.",
          },
        });
      });
  }, [executeMutation, order, institutionAccountId]);

  const orderType = TradeOptions.find(
    (o) => o.value === tradePreview.type.toLowerCase()
  );

  const tradeType =
    props.state.currentTradeType[0].toUpperCase() +
    props.state.currentTradeType.slice(1);

  return (
    <div>
      <h3 data-cy="order-details-title">Order Details</h3>

      <SmallDataTable
        data={[
          ["Symbol", tradePreview.symbol],
          ["Action", tradeType],
          ["Order Type", orderType?.label || "Unknown"],
          ["Shares", tradePreview.quantity.toString()],
          ...[
            tradePreview.limitPrice
              ? ["Limit Price", formatCurrency(tradePreview.limitPrice)]
              : [],
          ],
          ...[
            tradePreview.stopPrice
              ? ["Stop Price", formatCurrency(tradePreview.stopPrice)]
              : [],
          ],
          [
            "Market Price",
            formatCurrency(tradePreview.orderCost / tradePreview.quantity),
          ],
          ["Broker Commission", formatCurrency(tradePreview.commission)],
          ["Estimated Total", formatCurrency(tradePreview.cost)],
          [
            "Account",
            `${props.state.brokerName}  • • • • ${props.state.accountNumber}`,
          ],
        ]}
        displayOptions={{ isTwoColumnKeyValue: true }}
      />
      <div className={styles.buttonRow}>
        <Button onClick={() => props.dispatch({ type: "editOrder" })}>
          Edit
        </Button>
        <Button data-cy="submit-final-order" onClick={onSubmit}>
          Submit
        </Button>
      </div>
      {props.state.errorMessage && (
        <div>
          <p className={styles.errorMessage}>{props.state.errorMessage}</p>
        </div>
      )}
      <BrokerConnectionMessage brokerName={props.state.brokerName || ""} />
    </div>
  );
}
